import styled from '@emotion/styled';
import { formatHumanTimeDurationTwoUnits } from '@racemap/utilities/formattings/times';
import luxonGenerateConfig from '@rc-component/picker/generate/luxon';
import type { PickerFocusEventHandler } from '@rc-component/picker/interface';
import { ConfigProvider, DatePicker } from 'antd';
import type { Position } from 'geojson';
import type { DateTime } from 'luxon';
import {
  type CSSProperties,
  type FC,
  type ReactElement,
  type ReactNode,
  cloneElement,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useElementDimensions } from '../../../lib/customHooks';
import { staticThemeToken } from '../../../lib/theme';
import { LocalizedTimestampsPopover } from '../LocalizedTimestamps';
import { DraftFooter } from './DraftFooter';
import { ExtraPanelHeader } from './ExtraPanelHeader';
import { type Half, halfColor, halfTheme, halfTint } from './halfColors';
import {
  type Range,
  applyKeepDuration,
  changedHalf,
  formatRange,
  isCommittable,
  isSameRange,
  needsSingleConfirm,
  normalizeRange,
  sortRange,
} from './rangeDraft';

const { RangePicker } = DatePicker.generatePicker<DateTime>(luxonGenerateConfig);
type RangePickerProps = React.ComponentProps<typeof RangePicker>;

export type Props = RangePickerProps & {
  showUTCTimestamp?: boolean;
  geoPosition?: Position | undefined;
  /** Shift the end by the same delta when the start moves, so the range keeps its length. */
  keepDuration?: boolean;
};

export const DateRangePicker: FC<Props> = ({
  id,
  showUTCTimestamp = false,
  geoPosition,
  keepDuration = false,
  onBlur,
  ...props
}) => {
  const pickerId = useId();
  const [activeHalf, setActiveHalf] = useState<'start' | 'end' | null>(null);
  const containerRef = useRef(null);
  const { width: containerWidth } = useElementDimensions(containerRef);

  // rc-picker confirms one half at a time and throws away the half that was edited but not
  // confirmed. We collect every panel edit as a draft and commit both ends at once. See #1662.
  const singleConfirm = needsSingleConfirm(props);
  const [draft, setDraft] = useState<Range | null>(null);
  const draftRef = useRef<Range | null>(null);
  const [internalOpen, setInternalOpen] = useState(props.defaultOpen ?? false);
  const [uncontrolledValue, setUncontrolledValue] = useState(props.defaultValue);
  const discardRef = useRef(false);
  const closingRef = useRef(false);
  const switchingRef = useRef(false);
  const endEditedRef = useRef(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const committed = props.value !== undefined ? props.value : uncontrolledValue;
  // `committed` either way: an uncontrolled picker has no props.value to read the range from,
  // and the format, the time zone popover and the duration all describe what is on screen.
  const displayValue = singleConfirm ? (draft ?? committed) : committed;
  const mergedOpen = props.open ?? internalOpen;
  const mergedFormat = props.format || getPickerFormat(displayValue, containerWidth);
  const [startTime, endTime] = displayValue || [];
  const duration =
    startTime != null && endTime != null
      ? formatHumanTimeDurationTwoUnits(endTime.diff(startTime).toMillis())
      : '';

  useEffect(() => {
    // the inputs of the date range picker are not clearly identifiable
    // we add here now a unique id to each input
    const inputs = document.querySelectorAll(
      `#${containerPrefix}${CSS.escape(pickerId)} .ant-picker-input > input`,
    );
    for (const [index, input] of inputs.entries()) {
      input.id = getInputId(pickerId, index === 0 ? 'start' : 'end');
    }
  }, []);

  // The blur timer below reads the document, so it must not outlive the component.
  useEffect(() => () => clearTimeout(blurTimeoutRef.current), []);

  const putDraft = (next: Range | null) => {
    draftRef.current = next;
    setDraft(next);
  };

  // rc-picker re-opens the popup on the other half after a close it considers unfinished, from an
  // effect a render or two later. Two frames, since one loses the race on a busy main thread.
  const holdClosing = () => {
    closingRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        closingRef.current = false;
      });
    });
  };

  const commit = (next: Range | null) => {
    putDraft(null);

    if (next == null) {
      setUncontrolledValue(undefined);
      props.onChange?.(null, ['', '']);
      return;
    }

    const ordered = sortRange(next, props.order !== false);
    if (!isCommittable(ordered, props.allowEmpty)) return;
    if (isSameRange(ordered, committed)) return;

    if (props.value === undefined) setUncontrolledValue(ordered);
    props.onChange?.(ordered, formatRange(ordered, mergedFormat));
  };

  const handleCalendarChange: RangePickerProps['onCalendarChange'] = (dates, strings, info) => {
    props.onCalendarChange?.(dates, strings, info);
    if (!mergedOpen || closingRef.current) return;

    const previous = draftRef.current ?? normalizeRange(committed);
    const moved = changedHalf(previous, dates);
    if (moved === 'end' || moved === 'both') endEditedRef.current = true;

    // Dragging the start keeps the length, but never overrules an end the user just set.
    const shift = keepDuration && !endEditedRef.current;
    putDraft(
      shift
        ? applyKeepDuration(previous, dates, { minDate: props.minDate, maxDate: props.maxDate })
        : dates,
    );
  };

  /** Commit the draft, unless the interaction was cancelled. Runs before `onOpenChange`. */
  const flushDraft = (discard: boolean) => {
    const pending = draftRef.current;
    putDraft(null);
    if (!discard && pending != null) commit(pending);
  };

  const handleFocusCapture = (event: React.FocusEvent<HTMLElement>) => {
    const half = ownInputHalf(event.target, pickerId);
    if (half == null) return;

    // Sticky: clicking a date cell moves focus to rc's tabIndex={-1} panel, which must not read
    // as "no half is being edited".
    setActiveHalf(half);
    if (!singleConfirm) return;

    // rc force-closes once both halves have been visited; moving between them is not a close.
    closingRef.current = false;
    switchingRef.current = true;
    queueMicrotask(() => {
      switchingRef.current = false;
    });
  };

  // rc preventDefaults mousedown outside the inputs and only force-focuses the start when nothing
  // inside the selector is focused, so a click on the padding moves nothing. Forward it.
  const handleMouseDownCapture = (event: React.MouseEvent<HTMLElement>) => {
    if (!(event.target instanceof HTMLElement)) return;
    const half = event.target.closest(`.${inputClass}`);
    const input = half?.querySelector('input');
    if (input == null || input === event.target) return;
    input.focus();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && closingRef.current) return;
    if (!nextOpen && switchingRef.current) return;

    if (nextOpen) {
      putDraft(null);
      discardRef.current = false;
      endEditedRef.current = false;
    } else {
      holdClosing();
      flushDraft(discardRef.current);
      discardRef.current = false;
    }

    setInternalOpen(nextOpen);
    props.onOpenChange?.(nextOpen);
  };

  const closeFromFooter = (discard: boolean) => {
    holdClosing();
    flushDraft(discard);
    setInternalOpen(false);
    props.onOpenChange?.(false);
  };

  const handleKeyDown: RangePickerProps['onKeyDown'] = (event, preventDefault) => {
    // rc-picker resets the panel on Escape and only then closes, so the flag lands in time.
    if (event.key === 'Escape') discardRef.current = true;
    props.onKeyDown?.(event, preventDefault);
  };

  const handleClear = () => {
    commit(null);
    props.onClear?.();
  };

  const handleInputBlur: PickerFocusEventHandler = (e, info) => {
    // stop the propergation to prevent that the save event is triggered
    e.stopPropagation();

    // workaround. prevent blur if the focus switch von one input to another
    // trigger on blur only if the active element is not a date-range-picker-input
    clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => {
      const aElId = document.activeElement?.id;
      if (!aElId?.endsWith(`${inputIdMiddle}${pickerId}`)) {
        const container = document.getElementById(`${containerPrefix}${pickerId}`);
        container?.dispatchEvent(e.nativeEvent);
        onBlur?.(e, info);
      }
    }, 100);
  };

  const renderPanel =
    props.panelRender ||
    ((panelNode: ReactNode) => (
      <>
        <ExtraPanelHeader currentRange={activeHalf} />
        {panelNode}
      </>
    ));

  // The theme paints both endpoints in one colour, so the two are told apart here instead.
  // `originNode` is the cell's inner div — the element antd backgrounds — so a style on it wins
  // without a selector of ours.
  const renderCell: RangePickerProps['cellRender'] = (current, info) => {
    if (props.cellRender) return props.cellRender(current, info);
    if (info.type !== 'date') return info.originNode;

    const half = endpointHalf(normalizeRange(draft ?? committed), current as DateTime);
    if (half == null) return info.originNode;

    return cloneElement(info.originNode as ReactElement<{ style?: CSSProperties }>, {
      style: {
        backgroundColor: halfColor[half],
        // Out-of-month cells draw their text in the disabled grey, unreadable on a filled cell.
        color: staticThemeToken.colorTextLightSolid,
      },
    });
  };

  // Rendered through renderExtraFooter, not panelRender: TimeRangePicker replaces panelRender.
  const renderFooter: RangePickerProps['renderExtraFooter'] = (mode) => {
    const pending = normalizeRange(displayValue) ?? [null, null];

    return (
      <>
        {props.renderExtraFooter?.(mode)}
        <DraftFooter
          range={pending}
          canCommit={isCommittable(pending, props.allowEmpty)}
          onOk={() => closeFromFooter(false)}
          onCancel={() => closeFromFooter(true)}
        />
      </>
    );
  };

  return (
    <Container
      id={getContainerId(pickerId)}
      ref={containerRef}
      data-active-half={activeHalf ?? 'none'}
      onFocusCapture={handleFocusCapture}
      onMouseDownCapture={handleMouseDownCapture}
    >
      <ConfigProvider theme={halfTheme[activeHalf ?? 'start']}>
        <RangePicker
          id={id}
          {...props}
          // Records the range an uncontrolled picker settles on; the singleConfirm branch below
          // drops this again, since `commit` keeps that mode's value itself.
          onChange={(dates, strings) => {
            if (props.value === undefined) setUncontrolledValue(dates ?? undefined);
            props.onChange?.(dates, strings);
          }}
          cellRender={renderCell}
          {...(singleConfirm
            ? {
                needConfirm: false,
                // rc-picker refuses to leave a half that is still empty. Our commit is the one
                // that enforces completeness (isCommittable), so rc may always switch.
                allowEmpty: [true, true] as [boolean, boolean],
                value: displayValue,
                defaultValue: undefined,
                open: mergedOpen,
                onChange: undefined,
                onClear: handleClear,
                onCalendarChange: handleCalendarChange,
                onOpenChange: handleOpenChange,
                onKeyDown: handleKeyDown,
                renderExtraFooter: renderFooter,
              }
            : {})}
          onBlur={handleInputBlur}
          format={mergedFormat}
          panelRender={renderPanel}
        />
      </ConfigProvider>
      {(showUTCTimestamp || geoPosition != null || duration !== '') && (
        <BelowPicker>
          {(showUTCTimestamp || geoPosition != null) && (
            <LocalizedTimestampsPopover
              align="start"
              showUTC={showUTCTimestamp}
              timestamps={[
                { timestamp: startTime, label: 'Start Time' },
                { timestamp: endTime, label: 'End Time' },
              ]}
              position={
                geoPosition && {
                  lng: geoPosition[0],
                  lat: geoPosition[1],
                }
              }
            />
          )}
          {duration !== '' && <Duration title="Duration of the event">Δ {duration}</Duration>}
        </BelowPicker>
      )}
    </Container>
  );
};

const defaultFormat = 'yyyy-MM-dd HH:mm:ss' as const;
const onlyStartTimeFormat = 'HH:mm:ss' as const;
type FormatFunction = (value: DateTime) => string;

const getPickerFormat = (
  range: RangePickerProps['value'],
  inputWidth: number,
): string | FormatFunction => {
  if (range == null) return defaultFormat;
  const startTime = range[0];
  const endTime = range[1];
  if (startTime == null || endTime == null) return defaultFormat;
  const isSameDay = startTime?.hasSame(endTime, 'day');

  if (isSameDay && inputWidth < 400)
    return (value: DateTime) => {
      if (+value === +endTime) return value.toFormat(onlyStartTimeFormat);
      return value.toFormat(defaultFormat);
    };

  return defaultFormat;
};

// The time zone hint and the duration share one row under the input, each hugging its own edge.
const BelowPicker = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const Duration = styled.div`
  font-size: 0.85em;
  color: ${staticThemeToken.colorTextDescription};
  white-space: nowrap;
`;

const Container = styled.div`
  --half-tint: ${halfTint.start};

  &[data-active-half='end'] {
    --half-tint: ${halfTint.end};
  }

  /* All three size or fill the input wrapper, for which antd exposes no slot — classNames.input
     lands on the <input> itself, inside the padding these rules set. */
  .ant-picker-range {
    padding: 0 11px 0%;
  }

  .ant-picker-input {
    padding: 4px 0;

    :first-of-type {
      min-width: 170px;
    }
  }

  .ant-picker-focused .ant-picker-input-active {
    background-color: var(--half-tint);
  }
`;

const inputIdMiddle = '-date-range-picker-input-';
const containerPrefix = 'date-range-picker-';
const inputClass = 'ant-picker-input';

const getInputId = (pickerId: string, range: 'start' | 'end') =>
  `${range}${inputIdMiddle}${pickerId}`;
const getContainerId = (pickerId: string) => `${containerPrefix}${pickerId}`;

/** Which endpoint of the range this day is; a one-day range keeps the start colour. */
const endpointHalf = (range: Range | null, day: DateTime): Half | null => {
  const [start, end] = range ?? [null, null];
  if (start?.hasSame(day, 'day')) return 'start';
  if (end?.hasSame(day, 'day')) return 'end';
  return null;
};

/** Which of our two inputs this element is, or null when it is neither. */
const ownInputHalf = (element: HTMLElement, pickerId: string): 'start' | 'end' | null => {
  if (!element.id.endsWith(`${inputIdMiddle}${pickerId}`)) return null;
  return element.id.startsWith('start') ? 'start' : 'end';
};
